/**
 * `useMySchedule` — TanStack Query hook for the caller's published
 * roster assignments (`GET /api/account/me/schedule`).
 *
 * Introduced in IA-rework PR 4 (#200) to back the
 * `<WhatNeedsYouSection/>` panel section's "upcoming shifts" tally.
 * `<PersonalRoster/>` currently uses a raw `useEffect` + `apiFetch` to
 * read the same endpoint; promoting that consumer onto this shared
 * hook is a separate cleanup (the panel section + PersonalRoster
 * cache-share without it because they run on different routes — the
 * panel only mounts on `/me/*`, PersonalRoster lives at `/me`, but
 * the panel is up the moment you land anywhere under `/me/*`).
 *
 * Cache-key shape mirrors the established pattern (`useTeam`, `useCycle`,
 * `useCycleRoster`): `["me-schedule", accountId]`. `staleTime: 60_000`
 * matches `useWorkspaces` so the member panel re-renders cleanly when
 * either query refreshes — they tend to refresh together (workspace
 * change + new schedule).
 *
 * `enabled: Boolean(accountId)` so a logged-out user (no dev-shim id,
 * no Clerk session) doesn't fire a 401 fetch on every mount.
 */
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "../lib/AuthContext";
import { type ApiError, apiFetch } from "./client";

export interface MyScheduleAssignment {
  date: string;
  role: string;
  session: string | null;
  team_id: string;
  team_name: string;
  cycle_id: string;
  membership_id: string;
  is_published: boolean;
  /** Server-stamped shift category (#1605); absent on older payloads. */
  kind?: string | null;
  is_oncall?: boolean | null;
  counts_as_nothing?: boolean | null;
}

export interface MyScheduleResponse {
  account_id: string;
  horizon_start: string;
  horizon_days: number;
  assignments: MyScheduleAssignment[];
  ics_url: string;
  comparison: {
    me_vs_median_hours: number | null;
    transparent: boolean;
  };
}

const STALE_TIME_MS = 60_000;

export function meScheduleKey(accountId: string | null) {
  return ["me-schedule", accountId ?? ""] as const;
}

/** `enabled: false` holds the read (the panel handle's badge passes it on
 *  lead routes, where the member's schedule isn't needed — #1920). */
export function useMySchedule(opts: { enabled?: boolean } = {}) {
  const { accountId } = useAuth();
  return useQuery<MyScheduleResponse, ApiError | Error>({
    queryKey: meScheduleKey(accountId),
    enabled: Boolean(accountId) && (opts.enabled ?? true),
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () => apiFetch<MyScheduleResponse>("/api/account/me/schedule"),
  });
}
